import { Alert, Box, Button, Chip, Link, Stack, Typography } from '@mui/material';
import { DUPLICATE_LEVEL_LABELS, duplicateSignalLabel, type DuplicateDto, type DuplicateLevel, type HouseholdDetail } from '@hv/shared';
import { useResolveDuplicate } from '../api/hooks';
import { useAuth } from '../components/AuthContext';
import { useNotify } from '../components/Notify';
import { useUrlState } from '../urlState';
import { Section } from './Section';

const LEVEL_COLOR: Record<DuplicateLevel, 'default' | 'info' | 'warning' | 'error'> = {
  possible: 'default',
  moderate: 'info',
  strong: 'warning',
  very_strong: 'error',
};

export function DuplicateScore({ d }: { d: DuplicateDto }) {
  if (d.score === null) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Stack direction="row" gap={1} alignItems="center" data-testid="duplicate-score">
      <Typography variant="body2" fontWeight={600}>
        {d.score}
      </Typography>
      {d.level && <Chip size="small" color={LEVEL_COLOR[d.level]} label={DUPLICATE_LEVEL_LABELS[d.level]} />}
    </Stack>
  );
}

export function DuplicateSignals({ d }: { d: DuplicateDto }) {
  if (!d.signals.length) return <Typography variant="body2">{d.reason}</Typography>;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2 }} aria-label="Matched signals">
      {d.signals.map((s) => (
        <Typography component="li" variant="body2" key={s.code}>
          {duplicateSignalLabel(s.code)} (+{s.weight})
        </Typography>
      ))}
    </Box>
  );
}

export function DuplicateActions({ d }: { d: DuplicateDto }) {
  const { can } = useAuth();
  const { notify, notifyError } = useNotify();
  const resolve = useResolveDuplicate();
  if (!can('duplicates.resolve')) return null;
  const run = (action: 'merge' | 'dismiss') =>
    resolve.mutate(
      { id: d.id, action },
      {
        onSuccess: () => notify('success', action === 'merge' ? `Merged prospect into ${d.householdName}` : 'Marked as not a duplicate'),
        onError: (err) => notifyError(err, action === 'merge' ? 'Merge failed' : 'Dismiss failed'),
      },
    );
  return (
    <Stack direction="row" gap={1}>
      <Button size="small" variant="contained" disabled={resolve.isPending} onClick={() => run('merge')}>
        Merge prospect
      </Button>
      <Button size="small" disabled={resolve.isPending} onClick={() => run('dismiss')}>
        Not a duplicate
      </Button>
    </Stack>
  );
}

export function DuplicatesSection({ h }: { h: HouseholdDetail }) {
  const { set } = useUrlState();
  if (!h.duplicates.length) return null;
  return (
    <Section title="Possible duplicates">
      <Stack gap={1}>
        {h.duplicates.map((d) => {
          const otherId = d.householdId === h.id ? d.candidateHouseholdId : d.householdId;
          const otherName = d.householdId === h.id ? d.candidateHouseholdName : d.householdName;
          return (
            <Alert key={d.id} severity="warning" action={<DuplicateActions d={d} />}>
              <Typography variant="body2">
                May be the same family as{' '}
                <Link component="button" onClick={() => set({ h: otherId }, false)}>
                  {otherName}
                </Link>
                .{d.score === null && ` ${d.reason}.`} Merging keeps {d.householdName} and moves Loom notes, recommendations, referrals and decisions from the prospect {d.candidateHouseholdName}.
              </Typography>
              {d.score !== null && (
                <Stack gap={0.5} mt={1}>
                  <DuplicateScore d={d} />
                  <DuplicateSignals d={d} />
                </Stack>
              )}
            </Alert>
          );
        })}
      </Stack>
    </Section>
  );
}
